import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB' };

export default function VerifyTutorPage() {
  const { tutorIdNumber } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [pin, setPin]           = useState('');
  const [checkinResult, setCheckinResult] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    api.get(`/api/verify-tutor/${tutorIdNumber}`)
      .then(r => setData(r.data))
      .catch(() => setData({ valid: false }))
      .finally(() => setLoading(false));
  }, [tutorIdNumber]);

  const handlePinDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const digits = pin.split('');
    digits[i] = val;
    const newPin = digits.join('').slice(0, 4);
    setPin(newPin);
    setPinError('');
    if (val && i < 3) pinRefs[i + 1].current?.focus();
    if (newPin.length === 4) submitPin(newPin);
  };

  const handlePinKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !pin[i] && i > 0) {
      pinRefs[i - 1].current?.focus();
      const digits = pin.split('');
      digits[i - 1] = '';
      setPin(digits.join(''));
    }
  };

  const submitPin = async (pinValue) => {
    setCheckinLoading(true);
    setPinError('');
    setCheckinResult(null);
    try {
      const r = await api.post('/api/checkin', {
        tutor_id_number: tutorIdNumber,
        pin: pinValue || pin,
      });
      setCheckinResult(r.data);
      setPin('');
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed';
      if (msg.toLowerCase().includes('pin')) {
        setPinError('Wrong PIN — try again');
        setPin('');
        setTimeout(() => pinRefs[0].current?.focus(), 50);
      } else {
        setCheckinResult({ error: true, message: msg });
      }
    }
    setCheckinLoading(false);
  };

  const accentColor = data?.school_color || C.teal;

  return (
    <div style={{ minHeight:'100vh', background:'#F0F7FA', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ width:'100%', maxWidth:440 }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🎓</div>
          <div style={{ fontSize:22, fontWeight:800, color:C.navy }}>SpeakIQ</div>
          <div style={{ fontSize:13, color:C.grey, marginTop:2 }}>Tutor ID Verification</div>
        </div>

        {loading ? (
          <div style={{ background:'#fff', borderRadius:16, padding:48, textAlign:'center', color:C.grey }}>Verifying ID…</div>
        ) : !data?.valid ? (
          <div style={{ background:'#fff', borderRadius:16, padding:40, textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,0.08)' }}>
            <div style={{ width:64, height:64, borderRadius:'50%', background:'#FEE2E2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:28 }}>❌</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.error, marginBottom:8 }}>ID Not Valid</div>
            <div style={{ fontSize:14, color:C.grey, lineHeight:1.6 }}>
              This tutor ID could not be verified. It may be invalid, expired, or the tutor's account may be inactive.
            </div>
          </div>
        ) : (
          <div style={{ background:'#fff', borderRadius:16, overflow:'hidden', boxShadow:'0 4px 24px rgba(0,0,0,0.10)' }}>
            {/* Top band */}
            <div style={{ background: accentColor, padding:'20px 24px', display:'flex', alignItems:'center', gap:16 }}>
              {data.photo_url ? (
                <img src={data.photo_url} alt="" style={{ width:56, height:56, borderRadius:'50%', objectFit:'cover', border:'3px solid rgba(255,255,255,0.4)' }} />
              ) : (
                <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(255,255,255,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'#fff', border:'3px solid rgba(255,255,255,0.4)' }}>
                  {(data.tutor_name||'T')[0].toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{data.tutor_name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:2 }}>{data.school || 'SpeakIQ School'}</div>
              </div>
              <div style={{ marginLeft:'auto', background:'rgba(255,255,255,0.2)', borderRadius:20, padding:'4px 12px', fontSize:12, fontWeight:700, color:'#fff' }}>
                ✅ Verified
              </div>
            </div>

            {/* Status */}
            <div style={{
              background: data.is_active ? '#F0FDF4' : '#FEF2F2',
              borderBottom: `1px solid ${data.is_active ? '#BBF7D0' : '#FECACA'}`,
              padding:'10px 24px', display:'flex', alignItems:'center', gap:8,
              fontSize:13, fontWeight:700, color: data.is_active ? '#065F46' : '#991B1B',
            }}>
              <span>{data.is_active ? '🟢' : '🔴'}</span>
              {data.is_active ? 'Active Tutor — Currently employed at this school' : 'Account Inactive'}
            </div>

            {/* Details */}
            <div style={{ padding:'20px 24px' }}>
              {[
                { label:'Tutor ID', value: data.tutor_id_number, mono: true },
                { label:'Specializations', value: (data.specializations||[]).join(', ') || 'General English' },
                { label:'Approved Since', value: data.approved_at ? new Date(data.approved_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '—' },
                { label:'School', value: data.school || '—' },
              ].map(row => (
                <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F1F5F9' }}>
                  <span style={{ fontSize:13, color:C.grey }}>{row.label}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:C.navy, fontFamily: row.mono ? 'monospace' : 'inherit', maxWidth:220, textAlign:'right' }}>{row.value}</span>
                </div>
              ))}
            </div>

            {/* Staff attendance section */}
            <div style={{ padding:'0 24px 24px' }}>
              <div style={{ background:'#F8FAFC', borderRadius:12, padding:16, border:'1px solid #E5E7EB' }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.grey, textTransform:'uppercase', letterSpacing:0.5, marginBottom:12 }}>
                  📋 Staff — Log Attendance
                </div>

                {checkinResult ? (
                  /* Result state */
                  <div style={{
                    borderRadius:10, padding:'14px 16px', textAlign:'center',
                    background: checkinResult.error ? '#FEF2F2' : checkinResult.action === 'checkin' ? '#F0FDF4' : '#EFF6FF',
                    border: `1px solid ${checkinResult.error ? '#FECACA' : checkinResult.action === 'checkin' ? '#BBF7D0' : '#BFDBFE'}`,
                  }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>
                      {checkinResult.error ? '❌' : checkinResult.action === 'checkin' ? '✅' : '👋'}
                    </div>
                    <div style={{ fontWeight:700, fontSize:16, color:C.navy }}>{checkinResult.message}</div>
                    {checkinResult.action === 'checkout' && checkinResult.duration_min != null && (
                      <div style={{ fontSize:13, color:'#1D4ED8', marginTop:6, fontWeight:600 }}>
                        ⏱ Time on site: {Math.floor(checkinResult.duration_min/60)}h {checkinResult.duration_min%60}m
                      </div>
                    )}
                    <button onClick={() => { setCheckinResult(null); setTimeout(() => pinRefs[0].current?.focus(), 50); }}
                      style={{ marginTop:12, padding:'6px 16px', background:'transparent', border:`1px solid ${C.grey}`, borderRadius:8, fontSize:12, color:C.grey, cursor:'pointer' }}>
                      Log another
                    </button>
                  </div>
                ) : (
                  /* PIN input */
                  <>
                    <div style={{ fontSize:13, color:C.grey, marginBottom:12, textAlign:'center' }}>
                      Enter your school PIN to check in / out
                    </div>
                    <div style={{ display:'flex', gap:10, justifyContent:'center', marginBottom:pinError ? 8 : 12 }}>
                      {[0,1,2,3].map(i => (
                        <input
                          key={i}
                          ref={pinRefs[i]}
                          type="tel"
                          inputMode="numeric"
                          maxLength={1}
                          value={pin[i] || ''}
                          onChange={e => handlePinDigit(i, e.target.value)}
                          onKeyDown={e => handlePinKeyDown(i, e)}
                          style={{
                            width:52, height:56, textAlign:'center', fontSize:24, fontWeight:800,
                            border: `2px solid ${pinError ? C.error : pin[i] ? accentColor : '#D1D5DB'}`,
                            borderRadius:10, outline:'none', background:'#fff', color:C.navy,
                          }}
                        />
                      ))}
                    </div>
                    {pinError && (
                      <div style={{ textAlign:'center', fontSize:13, color:C.error, fontWeight:600, marginBottom:8 }}>
                        {pinError}
                      </div>
                    )}
                    <div style={{ textAlign:'center', fontSize:11, color:C.grey }}>
                      {checkinLoading ? 'Logging…' : 'PIN auto-submits after 4 digits'}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{ background:'#F9FAFB', borderTop:'1px solid #E5E7EB', padding:'14px 24px', textAlign:'center', fontSize:11, color:C.grey }}>
              This verification is provided by SpeakIQ · speakiq.uk
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
